import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Gift, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { TrialShell, TrialAlert, TrialField } from '../components/trial/TrialShell';
import { TrialPlans } from '../components/trial/TrialPlans';
import { TrialProgress } from '../components/trial/TrialProgress';
import { trialApi, readTrialToken, saveTrialToken, trialError, startTrialGoogle } from '../components/trial/trialApi';

export default function TrialSignupPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const init = useRef(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: new URLSearchParams(location.search).get('email') || '', first_name: '', business_name: '' });
  const [token, setToken] = useState(readTrialToken);
  const [plan, setPlan] = useState('agency');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [restoring, setRestoring] = useState(!!(readTrialToken() || location.hash.includes('trial_token=')));
  const cancelled = new URLSearchParams(location.search).get('cancelled') === '1';

  const accept = (data) => {
    setForm({ email: data.email, first_name: data.first_name || '', business_name: data.business_name || '' });
    if (data.plan) setPlan(data.plan);
    if (data.checkout_completed) return navigate('/trial-onboarding', { replace: true });
    setStep(data.business_name && data.first_name ? 3 : 2);
  };
  useEffect(() => {
    const previous = document.title;
    document.title = '7-day agency trial + $10 bonus credits — Voxinity AI';
    return () => { document.title = previous; };
  }, []);
  useEffect(() => {
    const fromLink = new URLSearchParams(location.hash.slice(1)).get('trial_token');
    const saved = fromLink || readTrialToken();
    if (!saved || init.current === saved) return;
    init.current = saved;
    setRestoring(true);
    saveTrialToken(saved); setToken(saved);
    if (fromLink) navigate({ pathname: '/free-trial', search: location.search }, { replace: true });
    trialApi.post('/progress', { signup_token: saved }).then(({ data }) => accept(data))
      .catch(err => { setError(trialError(err)); sessionStorage.removeItem('voxinity_trial_token'); setToken(''); })
      .finally(() => setRestoring(false));
  }, [location.hash]);

  const update = key => e => setForm(current => ({ ...current, [key]: e.target.value }));
  const submit = async e => {
    e.preventDefault(); if (busy) return;
    setBusy(true); setError(''); setInfo('');
    try {
      if (step === 1) {
        const { data } = await trialApi.post('/capture', { email: form.email.trim(), signup_token: token || undefined });
        if (data.recovery_sent) { setInfo(data.message); return; }
        saveTrialToken(data.signup_token); setToken(data.signup_token); accept(data);
      } else if (step === 2) {
        const { data } = await trialApi.post('/profile', { signup_token: token, first_name: form.first_name.trim(), business_name: form.business_name.trim() });
        accept(data);
      } else {
        const { data } = await trialApi.post('/self-signup', { signup_token: token, plan });
        if (data.onboarding) navigate('/trial-onboarding');
        else window.location.assign(data.checkout_url);
      }
    } catch (err) { setError(trialError(err)); } finally { setBusy(false); }
  };
  const google = async () => {
    setBusy(true); setError('');
    try { await startTrialGoogle(); } catch (err) { setError(trialError(err)); setBusy(false); }
  };

  return <TrialShell planStep={step === 3} focused={step > 1 || restoring}>
    <div data-testid="trial-signup-page">
      {restoring ? <div className="trial-loading" data-testid="trial-restoring"><Loader2 className="animate-spin" size={20} /> Restoring your signup…</div> : <>
        {step > 1 && <button type="button" className="trial-back" onClick={() => { setStep(step - 1); setError(''); }} disabled={busy} data-testid="trial-back-button"><ArrowLeft size={14} /> Back</button>}
        <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .2 }}>
          {step > 1 && <TrialProgress current={step - 1} />}
          {step === 1 && <p className="trial-offer" data-testid="trial-offer-badge"><Gift className="trial-offer-icon" size={16} strokeWidth={1.5} aria-hidden="true" /><span data-testid="trial-offer-text"><strong data-testid="trial-offer-amount">$10</strong> in free Calling &amp; Chat</span></p>}
          <h1 className="trial-heading" data-testid="trial-headline">{step === 1 ? 'Create an account' : step === 2 ? 'About your agency' : 'Choose your plan'}</h1>
          {step === 2 && <p className="trial-email-line" data-testid="trial-account-email">{form.email}</p>}
          <TrialAlert info>{info || (cancelled && step === 3 ? 'Your details are saved. Choose a plan to continue checkout.' : '')}</TrialAlert>
          <TrialAlert>{error}</TrialAlert>
          <form onSubmit={submit} data-testid="trial-signup-form">
            {step === 1 && <TrialField label="Email address" id="field-email" type="email" autoComplete="email" placeholder="Email address" value={form.email} onChange={update('email')} required maxLength={254} disabled={busy} />}
            {step === 2 && <><TrialField label="First name" id="field-first-name" autoComplete="given-name" placeholder="First name" value={form.first_name} onChange={update('first_name')} required maxLength={80} disabled={busy} /><TrialField label="Agency / business name" id="field-business-name" autoComplete="organization" placeholder="Agency / business name" value={form.business_name} onChange={update('business_name')} required minLength={2} maxLength={160} disabled={busy} /></>}
            {step < 3 ? <button type="submit" className="trial-primary" disabled={busy} data-testid={step === 1 ? 'email-continue-button' : 'profile-continue-button'}>{busy ? <Loader2 size={17} className="animate-spin" /> : <>{step === 1 ? 'Start My 7-Day Trial' : 'Continue'} <ArrowRight size={16} /></>}</button> : <TrialPlans plan={plan} setPlan={setPlan} busy={busy} />}
          </form>
          {step === 1 && <><p className="trial-login" data-testid="trial-login-prompt">Already have an account? <Link to="/login" data-testid="login-link">Log in</Link></p><div className="trial-divider" aria-hidden="true">OR</div><button type="button" className="trial-secondary" onClick={google} disabled={busy} data-testid="google-continue-button"><img src="/assets/brand/google-g.png" className="trial-google-mark" width="20" height="20" alt="" aria-hidden="true" data-testid="google-brand-logo" /> Continue with Google</button></>}
        </motion.div>
      </>}
    </div>
  </TrialShell>;
}